/**
 * World Map & Environment Renderer: "Desa Terbengkalai / Ancient Ruins"
 * 
 * Features:
 * - 1600x1200 expansive 2D top-down map.
 * - Paved stone ruins, grass clearings, ruins pillars.
 * - River with lily pads crossed by a solid wooden bridge.
 * - Braziers & torches with warm flickering light overlays.
 * - Collision detection for solid obstacles (water, pillars, ruins walls).
 */

import { MAP_WIDTH, MAP_HEIGHT } from "../config/gameConfig";

export interface StaticObstacle {
  x: number;
  y: number;
  width: number;
  height: number;
  type: "wall" | "pillar" | "crate" | "water";
}

export interface TorchLight {
  x: number;
  y: number;
  radius: number;
  color: string;
}

export class GameMap {
  public width: number = MAP_WIDTH;
  public height: number = MAP_HEIGHT;

  public obstacles: StaticObstacle[] = [];
  public torches: TorchLight[] = [];

  // Bridge passage coordinates: bridge crosses river from x=280 to x=420, y=360 to y=620
  public bridgeRect = { x: 270, y: 360, width: 140, height: 260 };

  constructor() {
    this.buildMapLayout();
  }

  private buildMapLayout() {
    // 1. River running vertically through the western side (x: 290 to 390, full height)
    // Water obstacles placed above and below the bridge
    this.obstacles.push(
      { x: 290, y: 0, width: 100, height: 360, type: "water" },
      { x: 290, y: 620, width: 100, height: MAP_HEIGHT - 620, type: "water" }
    );

    // 2. Ancient Stone Pillars and Ruins Walls
    const pillars = [
      { x: 180, y: 220 }, { x: 240, y: 220 },
      { x: 180, y: 720 }, { x: 240, y: 720 },
      { x: 520, y: 180 }, { x: 620, y: 180 }, { x: 720, y: 180 },
      { x: 520, y: 380 }, { x: 720, y: 380 },
      { x: 920, y: 240 }, { x: 1040, y: 240 }, { x: 1160, y: 240 },
      { x: 920, y: 480 }, { x: 1160, y: 480 },
      { x: 580, y: 780 }, { x: 740, y: 780 }, { x: 900, y: 780 },
      { x: 580, y: 980 }, { x: 900, y: 980 },
      { x: 1120, y: 820 }, { x: 1260, y: 820 }, { x: 1380, y: 820 },
    ];

    for (const p of pillars) {
      this.obstacles.push({
        x: p.x - 18,
        y: p.y - 18,
        width: 36,
        height: 36,
        type: "pillar",
      });
    }

    // 3. Ancient Village Houses & Ruin Walls
    this.obstacles.push(
      // Left ruin outpost
      { x: 80, y: 320, width: 120, height: 24, type: "wall" },
      { x: 80, y: 320, width: 24, height: 180, type: "wall" },
      // North fortress wall section
      { x: 500, y: 100, width: 340, height: 32, type: "wall" },
      // East abandoned camp structures
      { x: 1280, y: 300, width: 140, height: 90, type: "wall" },
      { x: 1240, y: 520, width: 160, height: 100, type: "wall" },
      // Southern ruin sanctum
      { x: 660, y: 1020, width: 280, height: 32, type: "wall" },
      // Crates and Barrels clusters
      { x: 440, y: 410, width: 32, height: 32, type: "crate" },
      { x: 476, y: 410, width: 30, height: 30, type: "crate" },
      { x: 780, y: 220, width: 40, height: 35, type: "crate" },
      { x: 1020, y: 510, width: 36, height: 36, type: "crate" }
    );

    // 4. Torches and Braziers (casting warm light)
    this.torches = [
      { x: 265, y: 360, radius: 140, color: "rgba(255, 160, 40, 0.45)" }, // Bridge North
      { x: 265, y: 620, radius: 140, color: "rgba(255, 160, 40, 0.45)" }, // Bridge South
      { x: 425, y: 360, radius: 140, color: "rgba(255, 160, 40, 0.45)" },
      { x: 425, y: 620, radius: 140, color: "rgba(255, 160, 40, 0.45)" },
      { x: 620, y: 200, radius: 180, color: "rgba(255, 150, 30, 0.5)" },  // Clearing north torch
      { x: 840, y: 440, radius: 190, color: "rgba(255, 150, 30, 0.55)" }, // Center ruins campfire
      { x: 1040, y: 260, radius: 160, color: "rgba(255, 140, 30, 0.45)" },
      { x: 740, y: 880, radius: 170, color: "rgba(255, 150, 30, 0.5)" },  // South sanctum torch
      { x: 1200, y: 720, radius: 160, color: "rgba(255, 150, 30, 0.45)" }, // East camp
    ];
  }

  /**
   * Collision verification: Checks if an entity with radius r at (x, y) overlaps with solid obstacles.
   */
  public isSolid(x: number, y: number, radius: number = 16): boolean {
    // Map bounds check
    if (x - radius < 30 || x + radius > this.width - 30) return true;
    if (y - radius < 30 || y + radius > this.height - 30) return true;

    // Check if player is safely on the wooden bridge (bridges override river water)
    const onBridge =
      x >= this.bridgeRect.x &&
      x <= this.bridgeRect.x + this.bridgeRect.width &&
      y >= this.bridgeRect.y &&
      y <= this.bridgeRect.y + this.bridgeRect.height;

    for (const ob of this.obstacles) {
      if (ob.type === "water" && onBridge) {
        continue; // Permitted over water via bridge
      }

      // Circle vs AABB collision
      const closestX = Math.max(ob.x, Math.min(x, ob.x + ob.width));
      const closestY = Math.max(ob.y, Math.min(y, ob.y + ob.height));
      const distX = x - closestX;
      const distY = y - closestY;

      if (distX * distX + distY * distY < radius * radius) {
        return true;
      }
    }

    return false;
  }

  /**
   * Attempt to slide movement along walls when bumping into obstacles
   */
  public resolveMovement(
    curX: number,
    curY: number,
    targetX: number,
    targetY: number,
    radius: number
  ): { x: number; y: number } {
    // Try full movement first
    if (!this.isSolid(targetX, targetY, radius)) {
      return { x: targetX, y: targetY };
    }

    // Try sliding horizontally
    if (!this.isSolid(targetX, curY, radius)) {
      return { x: targetX, y: curY };
    }

    // Try sliding vertically
    if (!this.isSolid(curX, targetY, radius)) {
      return { x: curX, y: targetY };
    }

    // Stuck
    return { x: curX, y: curY };
  }

  /**
   * Render the environment terrain, paths, river, ruins, bridge, and torches
   */
  public render(ctx: CanvasRenderingContext2D, viewX: number, viewY: number, viewW: number, viewH: number, now: number) {
    ctx.save();

    // 1. Deep Overgrown Earth Base
    ctx.fillStyle = "#152016";
    ctx.fillRect(0, 0, this.width, this.height);

    // 2. High-Polish Terrain Tiles
    const tileSize = 64;
    const startTileX = Math.floor(viewX / tileSize) * tileSize;
    const startTileY = Math.floor(viewY / tileSize) * tileSize;
    const endTileX = viewX + viewW + tileSize;
    const endTileY = viewY + viewH + tileSize;

    for (let tx = startTileX; tx < endTileX; tx += tileSize) {
      for (let ty = startTileY; ty < endTileY; ty += tileSize) {
        const hash = ((tx * 73856093) ^ (ty * 19349663)) >>> 0;
        const isPath =
          (tx > 370 && tx < 1090 && ty > 330 && ty < 690) || // Main village plaza
          (tx > 630 && tx < 870 && ty > 150 && ty < 1030);   // North-south avenue

        if (isPath) {
          // Weathered Ancient Flagstone Pavers
          const baseColor = (hash & 1) ? "#2e3430" : "#353c37";
          ctx.fillStyle = baseColor;
          ctx.fillRect(tx, ty, tileSize, tileSize);

          // Beveled stone slab edges
          ctx.strokeStyle = "#1a201b";
          ctx.lineWidth = 2;
          ctx.strokeRect(tx + 1, ty + 1, tileSize - 2, tileSize - 2);

          // Top-left highlight bevel
          ctx.strokeStyle = "rgba(120, 140, 130, 0.25)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(tx + 2, ty + tileSize - 3);
          ctx.lineTo(tx + 2, ty + 2);
          ctx.lineTo(tx + tileSize - 3, ty + 2);
          ctx.stroke();

          // Cracks in ancient stone
          if ((hash % 3) === 0) {
            ctx.strokeStyle = "#161b17";
            ctx.lineWidth = 1;
            const cx = tx + 14 + (hash % 28);
            const cy = ty + 14 + ((hash >> 3) % 28);
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx + 8, cy + 6);
            ctx.lineTo(cx + 14, cy + 4);
            ctx.stroke();
          }

          // Moss creeping in mortar
          if ((hash % 4) === 0) {
            ctx.fillStyle = "#253d23";
            ctx.fillRect(tx + (hash % 40), ty + 1, 10, 3);
          }

          // Glowing Arcane Runic Inscriptions (Ancient Anomaly Leylines)
          if ((hash % 11) === 0) {
            const runePulse = 0.35 + Math.sin(now * 0.003 + hash) * 0.25;
            ctx.save();
            ctx.strokeStyle = `rgba(0, 240, 255, ${runePulse})`;
            ctx.lineWidth = 1.5;
            ctx.shadowColor = "#00e5ff";
            ctx.shadowBlur = 6;
            ctx.beginPath();
            const rx = tx + 32;
            const ry = ty + 32;
            ctx.arc(rx, ry, 10, 0, Math.PI * 2);
            ctx.moveTo(rx - 7, ry);
            ctx.lineTo(rx + 7, ry);
            ctx.moveTo(rx, ry - 7);
            ctx.lineTo(rx, ry + 7);
            ctx.stroke();
            ctx.restore();
          }
        } else {
          // Lush Forest Grass Variation
          const grassColor = (hash % 3 === 0) ? "#1b291a" : (hash % 3 === 1) ? "#1f301e" : "#172416";
          ctx.fillStyle = grassColor;
          ctx.fillRect(tx, ty, tileSize, tileSize);

          // Grass blade tufts
          ctx.strokeStyle = "#2b4629";
          ctx.lineWidth = 1.5;
          const gx = tx + 10 + (hash % 36);
          const gy = ty + 12 + ((hash >> 2) % 36);
          ctx.beginPath();
          ctx.moveTo(gx, gy);
          ctx.lineTo(gx - 3, gy - 6);
          ctx.moveTo(gx + 4, gy);
          ctx.lineTo(gx + 6, gy - 7);
          ctx.moveTo(gx + 8, gy);
          ctx.lineTo(gx + 12, gy - 5);
          ctx.stroke();

          // Tiny wildflowers (amber / cyan / pink)
          if ((hash % 7) === 0) {
            const flowerColors = ["#ffd700", "#00f0ff", "#ff5588"];
            ctx.fillStyle = flowerColors[hash % flowerColors.length];
            ctx.beginPath();
            ctx.arc(gx + 14, gy - 2, 1.8, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    }

    // 3. Living Animated River
    const riverX = 290;
    const riverW = 100;

    // Deep multi-stop gradient for realistic water volume
    const riverGrad = ctx.createLinearGradient(riverX, 0, riverX + riverW, 0);
    riverGrad.addColorStop(0.0, "#0e2430");
    riverGrad.addColorStop(0.25, "#143a4a");
    riverGrad.addColorStop(0.5, "#1c4e62");
    riverGrad.addColorStop(0.75, "#143a4a");
    riverGrad.addColorStop(1.0, "#0e2430");
    ctx.fillStyle = riverGrad;
    ctx.fillRect(riverX, 0, riverW, this.height);

    // Shoreline wet sand & foam borders
    ctx.fillStyle = "rgba(40, 70, 75, 0.4)";
    ctx.fillRect(riverX - 4, 0, 5, this.height);
    ctx.fillRect(riverX + riverW - 1, 0, 5, this.height);

    // Flowing Water Waves & Caustics (Sine Wave Shimmer)
    ctx.strokeStyle = "rgba(140, 225, 255, 0.35)";
    ctx.lineWidth = 1.5;
    const waveSpeed = now * 0.04;
    for (let wy = -20; wy < this.height + 40; wy += 36) {
      const actualY = (wy + waveSpeed) % (this.height + 40);
      // Don't draw waves directly under the solid bridge deck
      if (actualY < this.bridgeRect.y + 4 || actualY > this.bridgeRect.y + this.bridgeRect.height - 4) {
        ctx.beginPath();
        for (let wx = 12; wx < riverW - 12; wx += 8) {
          const dy = Math.sin((wx + actualY * 0.5) * 0.15) * 3;
          if (wx === 12) ctx.moveTo(riverX + wx, actualY + dy);
          else ctx.lineTo(riverX + wx, actualY + dy);
        }
        ctx.stroke();
      }
    }

    // Floating Animated Lily Pads with Pink Lotus Blossoms
    const lilyPads = [
      { x: 325, y: 130 }, { x: 355, y: 260 },
      { x: 330, y: 730 }, { x: 365, y: 890 }, { x: 335, y: 1060 }
    ];
    for (const lp of lilyPads) {
      const bob = Math.sin(now * 0.003 + lp.x) * 2;
      ctx.fillStyle = "#2d6b38";
      ctx.beginPath();
      ctx.arc(lp.x, lp.y + bob, 9, 0.35, Math.PI * 2);
      ctx.fill();
      // Lotus flower blossom
      ctx.fillStyle = "#ff77aa";
      ctx.beginPath();
      ctx.arc(lp.x - 2, lp.y + bob - 2, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(lp.x - 2, lp.y + bob - 2, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }

    // 4. Shaded Timber Bridge Over River
    const b = this.bridgeRect;

    // Bridge cast shadow onto water
    ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
    ctx.fillRect(b.x - 6, b.y + 6, b.width + 12, b.height);

    // Bridge Understructure Foundation
    ctx.fillStyle = "#281b12";
    ctx.fillRect(b.x - 2, b.y, b.width + 4, b.height);

    // Individual Shaded Timber Planks
    for (let py = b.y; py < b.y + b.height; py += 16) {
      const plankHash = ((py * 48271) % 100);
      ctx.fillStyle = (plankHash % 2 === 0) ? "#4e3621" : "#563d26";
      ctx.fillRect(b.x, py, b.width, 14);

      // Plank top highlight & bottom shadow
      ctx.fillStyle = "rgba(255, 255, 255, 0.08)";
      ctx.fillRect(b.x, py, b.width, 2);
      ctx.fillStyle = "#21160d";
      ctx.fillRect(b.x, py + 14, b.width, 2);

      // Iron nail rivets at plank ends
      ctx.fillStyle = "#1e2226";
      ctx.fillRect(b.x + 8, py + 6, 2.5, 2.5);
      ctx.fillRect(b.x + b.width - 10, py + 6, 2.5, 2.5);
    }

    // Heavy Oak Guardrails
    ctx.fillStyle = "#6d4e32";
    ctx.fillRect(b.x - 4, b.y, 8, b.height);
    ctx.fillRect(b.x + b.width - 4, b.y, 8, b.height);

    // Railing posts
    ctx.fillStyle = "#3d2a1b";
    for (let py = b.y; py <= b.y + b.height; py += 48) {
      ctx.fillRect(b.x - 6, py - 4, 12, 8);
      ctx.fillRect(b.x + b.width - 6, py - 4, 12, 8);
    }

    // 5. Solid Obstacles (2.5D Ruin Pillars, Stone Walls, Crates)
    for (const ob of this.obstacles) {
      if (ob.type === "water") continue;

      if (ob.type === "pillar") {
        // Ancient Corinthian Column
        // Ground cast shadow
        ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
        ctx.beginPath();
        ctx.ellipse(ob.x + ob.width / 2 + 6, ob.y + ob.height, ob.width * 0.75, 8, 0, 0, Math.PI * 2);
        ctx.fill();

        // Plinth / Base
        ctx.fillStyle = "#2a302c";
        ctx.fillRect(ob.x - 2, ob.y + ob.height - 6, ob.width + 4, 10);
        ctx.fillStyle = "#46504a";
        ctx.fillRect(ob.x - 1, ob.y + ob.height - 6, ob.width + 2, 3);

        // Fluted Shaft
        const shaftGrad = ctx.createLinearGradient(ob.x, 0, ob.x + ob.width, 0);
        shaftGrad.addColorStop(0, "#2f3632");
        shaftGrad.addColorStop(0.3, "#4d5750");
        shaftGrad.addColorStop(0.7, "#58635c");
        shaftGrad.addColorStop(1, "#2b312d");
        ctx.fillStyle = shaftGrad;
        ctx.fillRect(ob.x, ob.y - 24, ob.width, ob.height + 18);

        // Vertical fluting lines
        ctx.strokeStyle = "rgba(20, 25, 22, 0.45)";
        ctx.lineWidth = 1.5;
        for (let fx = ob.x + 6; fx < ob.x + ob.width - 4; fx += 7) {
          ctx.beginPath();
          ctx.moveTo(fx, ob.y - 22);
          ctx.lineTo(fx, ob.y + ob.height - 6);
          ctx.stroke();
        }

        // Carved Capital / Top
        ctx.fillStyle = "#636f67";
        ctx.fillRect(ob.x - 3, ob.y - 30, ob.width + 6, 8);
        ctx.fillStyle = "#333b36";
        ctx.fillRect(ob.x - 3, ob.y - 23, ob.width + 6, 2);

        // Creeping moss
        ctx.fillStyle = "#2d4d29";
        ctx.fillRect(ob.x + 3, ob.y - 4, 7, 10);
        ctx.fillRect(ob.x + ob.width - 8, ob.y - 14, 5, 8);
      } else if (ob.type === "wall") {
        // Ruined Fortress Wall
        ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
        ctx.fillRect(ob.x, ob.y + ob.height, ob.width, 16);

        // Wall Body with Stone Blocks
        ctx.fillStyle = "#353d38";
        ctx.fillRect(ob.x, ob.y - 20, ob.width, ob.height + 20);

        // Wall Top Cap
        ctx.fillStyle = "#4f5a52";
        ctx.fillRect(ob.x, ob.y - 24, ob.width, 6);

        // Mortar lines & Bricks
        ctx.strokeStyle = "#1e2420";
        ctx.lineWidth = 1.5;
        for (let by = ob.y - 18; by < ob.y + ob.height; by += 12) {
          ctx.beginPath();
          ctx.moveTo(ob.x, by);
          ctx.lineTo(ob.x + ob.width, by);
          ctx.stroke();
        }
        for (let bx = ob.x; bx < ob.x + ob.width; bx += 20) {
          ctx.beginPath();
          ctx.moveTo(bx, ob.y - 20);
          ctx.lineTo(bx, ob.y + ob.height);
          ctx.stroke();
        }

        // Hanging ivy vines
        ctx.fillStyle = "#2b4e28";
        ctx.beginPath();
        ctx.moveTo(ob.x + 12, ob.y - 20);
        ctx.lineTo(ob.x + 16, ob.y + 6);
        ctx.lineTo(ob.x + 8, ob.y + 14);
        ctx.fill();
      } else if (ob.type === "crate") {
        // Wooden Supply Crate with Metal Brackets
        ctx.fillStyle = "#5c4028";
        ctx.fillRect(ob.x, ob.y - 8, ob.width, ob.height + 8);

        // Bevel border
        ctx.strokeStyle = "#382516";
        ctx.lineWidth = 2;
        ctx.strokeRect(ob.x, ob.y - 8, ob.width, ob.height + 8);

        // Cross planks
        ctx.beginPath();
        ctx.moveTo(ob.x + 2, ob.y - 6);
        ctx.lineTo(ob.x + ob.width - 2, ob.y + ob.height + 6);
        ctx.moveTo(ob.x + ob.width - 2, ob.y - 6);
        ctx.lineTo(ob.x + 2, ob.y + ob.height + 6);
        ctx.stroke();

        // Iron corner brackets
        ctx.fillStyle = "#2c343b";
        ctx.fillRect(ob.x, ob.y - 8, 6, 6);
        ctx.fillRect(ob.x + ob.width - 6, ob.y - 8, 6, 6);
        ctx.fillRect(ob.x, ob.y + ob.height + 2, 6, 6);
        ctx.fillRect(ob.x + ob.width - 6, ob.y + ob.height + 2, 6, 6);
      }
    }

    // 6. Pine Trees with Lush Foliage
    const trees = [
      { x: 120, y: 120 }, { x: 220, y: 80 }, { x: 140, y: 700 }, { x: 80, y: 920 },
      { x: 460, y: 120 }, { x: 880, y: 100 }, { x: 1350, y: 140 }, { x: 1480, y: 260 },
      { x: 1420, y: 960 }, { x: 1280, y: 1080 }, { x: 440, y: 1040 }, { x: 180, y: 1100 },
    ];
    for (const tr of trees) {
      // Tree Ground Shadow
      ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
      ctx.beginPath();
      ctx.ellipse(tr.x + 8, tr.y + 24, 32, 14, 0, 0, Math.PI * 2);
      ctx.fill();

      // Gnarled Trunk
      ctx.fillStyle = "#302014";
      ctx.fillRect(tr.x - 6, tr.y - 8, 12, 30);
      ctx.fillStyle = "#4a3220";
      ctx.fillRect(tr.x - 4, tr.y - 8, 4, 30);

      // Layer 1: Deep Canopy Shadow
      ctx.fillStyle = "#122616";
      ctx.beginPath();
      ctx.arc(tr.x, tr.y - 4, 26, 0, Math.PI * 2);
      ctx.fill();

      // Layer 2: Mid-tier Foliage
      ctx.fillStyle = "#1d3e23";
      ctx.beginPath();
      ctx.arc(tr.x, tr.y - 16, 22, 0, Math.PI * 2);
      ctx.fill();

      // Layer 3: Sunlit Canopy Apex
      ctx.fillStyle = "#2d5e35";
      ctx.beginPath();
      ctx.arc(tr.x, tr.y - 28, 16, 0, Math.PI * 2);
      ctx.fill();

      // Top Highlight Rim
      ctx.fillStyle = "#3d7a46";
      ctx.beginPath();
      ctx.arc(tr.x - 4, tr.y - 32, 7, 0, Math.PI * 2);
      ctx.fill();
    }

    // 7. Stone Torch Stands & Flickering Brazier Fires
    for (const torch of this.torches) {
      // Pedestal
      ctx.fillStyle = "#232624";
      ctx.fillRect(torch.x - 4, torch.y - 2, 8, 18);
      ctx.fillStyle = "#3a403c";
      ctx.fillRect(torch.x - 6, torch.y - 4, 12, 4);

      // Iron Brazier Cup
      ctx.fillStyle = "#1b1e1c";
      ctx.beginPath();
      ctx.arc(torch.x, torch.y - 5, 8, 0, Math.PI);
      ctx.fill();

      // Flickering Flame Core
      const flicker = Math.sin(now * 0.016 + torch.x) * 2;
      ctx.fillStyle = "#ff4400";
      ctx.beginPath();
      ctx.arc(torch.x, torch.y - 9 + flicker, 6, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "#ffbb00";
      ctx.beginPath();
      ctx.arc(torch.x, torch.y - 10 + flicker, 4, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(torch.x, torch.y - 11 + flicker, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Render warm atmospheric ambient lighting from torches (drawn over ground)
   */
  public renderTorchLighting(ctx: CanvasRenderingContext2D, now: number) {
    ctx.save();
    ctx.globalCompositeOperation = "screen";

    for (const torch of this.torches) {
      const flickerRadius = torch.radius + Math.sin(now * 0.012 + torch.x) * 6;
      const lightGrad = ctx.createRadialGradient(
        torch.x,
        torch.y - 6,
        4,
        torch.x,
        torch.y - 6,
        flickerRadius
      );
      lightGrad.addColorStop(0, torch.color);
      lightGrad.addColorStop(0.4, "rgba(255, 140, 30, 0.18)");
      lightGrad.addColorStop(1, "rgba(0, 0, 0, 0)");

      ctx.fillStyle = lightGrad;
      ctx.beginPath();
      ctx.arc(torch.x, torch.y - 6, flickerRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
